
import { useState, useEffect } from 'react';
import { HiArrowUturnLeft } from "react-icons/hi2";
import { MdManageAccounts } from 'react-icons/md';
import { getUsersWithAccess, revokeSingleUser } from '../../../../../../api/businessAndEnterprise/sheetApi';
import ReactNotifications, { showApiErrors } from '../../../../../common/reactNotifications/ReactNotifications';
import * as utilConstants from '../../../../../../utils/constants/utilsConstants';
import DeleteModal from '../../../../../common/commonModal/DeleteModal';

export const ManageAccessTab = ({ sheetId }) => {
    const [accessList, setAccessList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRevokeConfirmation, setIsRevokeConfirmation] = useState(false);
    const [revokeUserId, setRevokeUserId] = useState(null);

    useEffect(() => {
        fetchSheetAccessList();
    }, [sheetId]);

    const fetchSheetAccessList = (page = 1) => {
        setLoading(true);
        getUsersWithAccess(sheetId, { page: page }, (response) => {
            if (response?.status === true) {
                setAccessList(response?.data || []);
            }
            setLoading(false);
        });
    };

    const handleRevokeAccess = () => {
        if (revokeUserId) {
            revokeSingleUser(sheetId, revokeUserId, (response) => {
                if (response?.status === true) {
                    ReactNotifications(response?.message, utilConstants.sucessState200);
                    setIsRevokeConfirmation(false);
                    fetchSheetAccessList();
                } else {
                    showApiErrors(response);
                }
            });
        }
    };

    const handleRevokeClick = (userId) => {
        if (userId) {
            setRevokeUserId(userId);
            setIsRevokeConfirmation(true);
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    if (accessList.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="mb-4 p-4 bg-gray-100 rounded-full">
                    <MdManageAccounts size={64} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No Users Yet</h3>
                <p className="text-gray-500 text-sm max-w-md">
                    Share access with internal or external users to see them listed here
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-600 font-medium">
                    {accessList.length} user{accessList.length > 1 ? 's' : ''} with access
                </div>
                <button
                    onClick={() => fetchSheetAccessList()}
                    className="text-sm text-black font-bold underline"
                >
                    Refresh
                </button>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {accessList.map(user => (
                    <div
                        key={user.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                    >
                        <div className="flex-grow">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-gray-800">{user.user_name}</span>
                                <span
                                    className="px-2 py-0.5 text-xs font-medium rounded-full"
                                    style={{
                                        backgroundColor: user.visibility === 'internal' ? '#E0F2FE' : '#FEF3C7',
                                        color: user.visibility === 'internal' ? '#0369A1' : '#92400E'
                                    }}
                                >
                                    {user.visibility === 'company' ? 'Internal' : 'External'}
                                </span>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">{user.email}</div>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span className="font-medium">
                                    Access: <span className="text-gray-700">{user.access_level === 'edit' ? 'Can Edit' : 'View Only'}</span>
                                </span>
                                {user.expires_at && (
                                    <span>• Expires: {new Date(user.expires_at).toLocaleDateString()}</span>
                                )}
                            </div>
                        </div>

                        <button
                            onClick={() => handleRevokeClick(user.id)}
                            className="ml-4 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2 font-medium text-sm"
                        >
                            <HiArrowUturnLeft size={18} />
                            Revoke
                        </button>
                    </div>
                ))}
            </div>
            <DeleteModal
                show={isRevokeConfirmation}
                onClose={() => setIsRevokeConfirmation(false)}
                onConfirm={handleRevokeAccess}
                confirmText='Revoke'
                title="Revoke Access"
                message="Are you sure you want to revoke this user access? This action cannot be undone."
                modalCentered={false}
            />
        </div>
    );
};